import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { Milestone, MilestoneCreateDto } from "../../modules/shared/model";
import { createMilestone as createMilestoneAction, updateMilestone as updateMilestoneAction, getMilestoneById as getMilestoneByIdAction, getMilestones as getMilestonesAction, deleteMilestone as deleteMilestoneAction, getMilestonesByContract as getMilestonesByContractAction } from "../Actions/MilestoneActions";

export const createMilestone = createAsyncThunk("milestones/create", async (data: MilestoneCreateDto, { rejectWithValue }) => {
	try { return await createMilestoneAction(data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to create milestone"); }
});

export const updateMilestone = createAsyncThunk("milestones/update", async ({ id, data }: { id: string; data: Partial<MilestoneCreateDto> }, { rejectWithValue }) => {
	try { return await updateMilestoneAction(id, data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to update milestone"); }
});

export const fetchMilestone = createAsyncThunk("milestones/getById", async (id: string, { rejectWithValue }) => {
	try { return await getMilestoneByIdAction(id); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch milestone"); }
});

export const fetchMilestones = createAsyncThunk("milestones/get", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
	try { return await getMilestonesAction(query); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch milestones"); }
});

export const fetchMilestonesByContract = createAsyncThunk("milestones/getByContract", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
	try { return await getMilestonesByContractAction(query); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch milestones by contract"); }
});

export const deleteMilestone = createAsyncThunk("milestones/delete", async (id: string, { rejectWithValue }) => {
	try { await deleteMilestoneAction(id); return id; } catch (e: any) { return rejectWithValue(e?.message || "Failed to delete milestone"); }
});

interface MilestonesState {
	items: Milestone[];
	current: Milestone | null;
	loading: boolean;
	error: string | null;
	totalCount?: number;
}

const initialState: MilestonesState = {
	items: [],
	current: null,
	loading: false,
	error: null,
	totalCount: 0,
};

const milestonesSlice = createSlice({
	name: "milestones",
	initialState,
	reducers: {
		clearMilestonesError(state) { state.error = null; },
		clearCurrentMilestone(state) { state.current = null; },
	},
	extraReducers: (builder) => {
		builder
			.addCase(createMilestone.pending, (s) => { s.loading = true; s.error = null; })
			.addCase(createMilestone.fulfilled, (s, a: PayloadAction<Milestone>) => { s.loading = false; s.items.unshift(a.payload); s.totalCount = (s.totalCount || 0) + 1; })
			.addCase(createMilestone.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

		builder
			.addCase(updateMilestone.pending, (s) => { s.loading = true; s.error = null; })
			.addCase(updateMilestone.fulfilled, (s, a: PayloadAction<Milestone>) => {
				s.loading = false;
				const i = s.items.findIndex(x => x.id === a.payload.id);
				if (i !== -1) s.items[i] = a.payload;
				if (s.current?.id === a.payload.id) s.current = a.payload;
			})
			.addCase(updateMilestone.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

		builder
			.addCase(fetchMilestone.pending, (s) => { s.loading = true; s.error = null; })
			.addCase(fetchMilestone.fulfilled, (s, a: PayloadAction<Milestone>) => { s.loading = false; s.current = a.payload; })
			.addCase(fetchMilestone.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

		builder
			.addCase(fetchMilestones.pending, (s) => { s.loading = true; s.error = null; })
			.addCase(fetchMilestones.fulfilled, (s, a: PayloadAction<{ data: Milestone[]; totalCount?: number }>) => { s.loading = false; s.items = a.payload.data || []; s.totalCount = a.payload.totalCount || 0; })
			.addCase(fetchMilestones.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

		builder
			.addCase(fetchMilestonesByContract.pending, (s) => { s.loading = true; s.error = null; })
			.addCase(fetchMilestonesByContract.fulfilled, (s, a: PayloadAction<{ data: Milestone[]; totalCount?: number }>) => { s.loading = false; s.items = a.payload.data || []; s.totalCount = a.payload.totalCount || 0; })
			.addCase(fetchMilestonesByContract.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

		builder
			.addCase(deleteMilestone.pending, (s) => { s.loading = true; s.error = null; })
			.addCase(deleteMilestone.fulfilled, (s, a: PayloadAction<string>) => { s.loading = false; s.items = s.items.filter(x => x.id !== a.payload); if (s.current?.id === a.payload) s.current = null; s.totalCount = Math.max(0, (s.totalCount || 0) - 1); })
			.addCase(deleteMilestone.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });
	}
});

export const { clearMilestonesError, clearCurrentMilestone } = milestonesSlice.actions;
export default milestonesSlice.reducer;

